import {Component, computed} from '@angular/core';
import {Button} from 'primeng/button';
import {ManageTaBaseComponent} from '../manage-ta-base-component.directive';
import {ApprovalContext} from '../../../../api';
import {hasPendingDayTripPayment} from '../../../../util/general.utils';

/**
 * The payment actions that sit outside the approval chain - processing a prepayment, recording an
 * overpayment deduction and (Compliance) recording a day trip payment. None is driven by the TA status: both stay outstanding
 * until someone actions them, so they must remain available while the TA is being finalised or
 * cancelled and after it has closed - finance can finalise an approved cancellation before
 * compliance has recorded the deduction. Status only ever rules an action out (see
 * closedWithoutPayout). Renders nothing when there is nothing left to action.
 */
@Component({
  selector: 'manage-ta-payment-actions',
  imports: [
    Button,
  ],
  templateUrl: './manage-ta-payment-actions.component.html',
})
export class ManageTaPaymentActionsComponent extends ManageTaBaseComponent {
  canProcessPrepayment = computed(() => this.permissionService.canProcessPrepayment());
  canProcessDeduction = computed(() => this.permissionService.canProcessDeduction());
  canProcessDayTripPayment = computed(() => this.permissionService.canProcessDayTripPayment());

  // prePaymentProcessed survives the partial merges that workflow responses do to the store's copy
  // of the TA, so it is the primary signal; the approval rows only need checking when it is absent.
  isPrepaymentProcessed = computed(() => (
    this.taSubmission()?.prePaymentProcessed === true ||
    this.approvalsFor(ApprovalContext.PrePayment).some(approval => approval.status === 'Actioned' && approval.actionType !== 'Cancelled')
  ));

  /**
   * A TA that closed without ever being paid out has no prepayment left to process. This matters
   * most for a cancelled TA: TAStore.ActionSubmissionWorkflow only cancels outright while the
   * prepayment is still unprocessed (a processed one is routed into the cancel-approval flow), so
   * every plainly-cancelled pre-paid TA would otherwise read as awaiting prepayment forever.
   *
   * Finalised is deliberately absent - a finalised TA's prepayment can still be outstanding, which
   * is why these actions are rendered on that status at all.
   */
  private closedWithoutPayout = computed(() => {
    const status = this.taSubmission()?.status;
    return status === 'Cancelled' || status === 'TravelRejected' || status === 'Deleted';
  });

  prepaymentOutstanding = computed(() => (
    !!this.taSubmission()?.isPrePaid && !this.isPrepaymentProcessed() && !this.closedWithoutPayout()
  ));

  /**
   * The server raises the OverPayment approval when the acquittal is approved, so a pending row is
   * the real signal. Comparing the values only stands in when the TA carries no overpayment
   * approval at all - the case the finalisation page has always used.
   */
  overpaymentOutstanding = computed(() => {
    const approvals = this.approvalsFor(ApprovalContext.OverPayment);
    if (approvals.length) {
      return approvals.some(approval => approval.status !== 'Actioned');
    }
    // The cancel flow always raises an OverPayment approval (TAStore.ApproveCancellationSubmission),
    // so a closed TA that has none never had a prepayment to recover - whatever its itinerary was
    // later revised down to.
    if (this.closedWithoutPayout()) {
      return false;
    }
    return !!this.taSubmission()?.isPrePaid && this.isOverpaid();
  });

  /**
   * Raised by the server on acquittal approval whenever a traveller claims the $45, including the
   * last day of a multi-day TA, so it can still be pending once Finance has finalised.
   */
  dayTripPaymentOutstanding = computed(() => hasPendingDayTripPayment(this.taSubmission()));

  hasOutstandingAction = computed(() =>
    this.prepaymentOutstanding() || this.overpaymentOutstanding() || this.dayTripPaymentOutstanding());

  confirmProcessPrepayment(): void {
    this.confirmationServiceWrapper.processPrepaymentConfirm(this.taSubmission()).subscribe({
      next: () => {},
      error: err => {
        console.error(err);
      },
    });
  }

  confirmProcessDeduction(): void {
    this.confirmationServiceWrapper.processDeductionConfirm(this.taSubmission()).subscribe({
      next: () => {},
      error: err => {
        console.error(err);
      },
    });
  }

  confirmProcessDayTripPayment(): void {
    this.confirmationServiceWrapper.processDayTripPaymentConfirm(this.taSubmission()).subscribe({
      next: () => {},
      error: err => {
        console.error(err);
      },
    });
  }

  private approvalsFor(context: ApprovalContext) {
    return (this.taSubmission()?.approvals ?? []).filter(approval => approval.actionContext === context);
  }
}
